import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, FileText, Trash2, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

interface UserSettingsSheetProps {
  children: React.ReactNode;
}

export function UserSettingsSheet({ children }: UserSettingsSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const initials = user?.fullName
    ? user.fullName
        .split(" ")
        .map((n: string) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "U";

  const handleDeleteAccount = async () => {
    track("delete_account_confirmed", { role: user?.role });
    try {
      await api.delete(`/users/account/${user?.userId}/delete-account`, user?.accessToken);
    } catch {
      // proceed with sign-out regardless
    }
    signOut();
    navigate("/login");
  };

  const rowClass =
    "flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-150";

  return (
    <>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>{children}</SheetTrigger>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="px-5 pt-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-easly-blue/80 to-primary flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow-sm">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-sm font-semibold text-foreground truncate">
                  {user?.fullName}
                </SheetTitle>
                {user?.email && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{user.email}</p>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
            <button
              onClick={() => {
                track("privacy_policy_clicked");
                setSheetOpen(false);
                navigate("/privacy-policy");
              }}
              className={rowClass}
            >
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{t("topBar.privacyPolicy")}</span>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            </button>

            <button
              onClick={() => {
                track("terms_of_service_clicked");
                setSheetOpen(false);
                navigate("/terms-of-service");
              }}
              className={rowClass}
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{t("topBar.termsOfService")}</span>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            </button>
          </div>

          <div className="px-3 pb-4 border-t border-border pt-3">
            <div>
              <button
                onClick={() => {
                  track("delete_account_dialog_opened");
                  setDeleteDialogOpen(true);
                }}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-destructive/70 hover:text-destructive hover:bg-destructive/[0.07] transition-all duration-150"
              >
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                <span>{t("topBar.deleteAccount")}</span>
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("topBar.deleteAccountTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("topBar.deleteAccountDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("topBar.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("topBar.deleteAccount")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
